import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { BRAND } from "../lib/constants";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset successful");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[#E2DAD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]";

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-[#E2DAD0] p-8">
        <div className="text-center mb-6">
          <img src={BRAND.logo} alt="logo" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#C5A059] mx-auto bg-white" />
          <h1 className="font-serif font-bold text-2xl text-[#1C2B20] mt-4">Reset Password</h1>
          <p className="text-sm text-[#5A6B5D] mt-1">Choose a new password for your account</p>
        </div>

        {!token ? (
          <div className="text-center text-[#C86D51]" data-testid="reset-invalid">
            This reset link is invalid or incomplete.
            <Link to="/forgot-password" className="block mt-4 text-[#1F4D2E] font-medium hover:underline">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 size={44} className="text-[#1F4D2E] mx-auto" />
            <p className="text-[#1C2B20] font-medium mt-4">Password updated! Redirecting to login…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">New Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">Confirm Password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" className={inputCls} />
            </div>
            {error && <p className="text-sm text-[#C86D51]" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] disabled:opacity-60 text-white font-medium rounded-full py-3 transition-colors">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} Update Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
